import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const LINKS = [
  { label: 'מדריכים', href: '/blog' },
  { label: 'שירותים', href: '/services' },
  { label: 'קנייה', href: '/shop' },
  { label: 'בונה מחשב', href: '/build-pc' },
  { label: 'מעקב תיקון', href: '/repair-status' },
  { label: 'צרו קשר', href: '/contact' },
];

// Dedicated B2B+local hub page (React route)
const B2B_LINK = { label: 'לעסקים', href: '/business/local' };

export default function CinHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the mobile drawer on Escape (a11y — IS 5568 / WCAG keyboard operability)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <header
      className={`font-heebo fixed top-0 z-50 w-full transition duration-300 ${
        scrolled
          ? 'border-b border-[var(--cin-line)] bg-white/[0.92] backdrop-blur-xl'
          : 'bg-white/[0.92] backdrop-blur-md'
      }`}
    >
      <div className="mx-auto max-w-[1280px] flex h-16 md:h-20 items-center justify-between px-5 md:px-8">
        {/* Logo */}
        <Link to="/" aria-label="בנה מחשבים — דף הבית" className="flex items-center gap-1.5 text-xl font-black tracking-tight text-[var(--cin-ink)]">
          <span className="cin-text-gradient-blue">בנה</span>
          <span>מחשבים</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1" aria-label="ניווט ראשי">
          {LINKS.map((l) => {
            const active = pathname === l.href;
            return (
              <Link
                key={l.href}
                to={l.href}
                className={`relative px-3 py-2 text-sm font-medium transition-colors ${
                  active ? 'text-[var(--cin-blue)]' : 'text-[var(--cin-muted)] hover:text-[var(--cin-ink)]'
                }`}
              >
                {l.label}
                {active && (
                  <span className="absolute inset-x-3 -bottom-0.5 h-[2px] rounded bg-[var(--cin-blue)]" />
                )}
              </Link>
            );
          })}
          {/* B2B link — separator + outbound to /business/ static landing */}
          <span className="mx-2 h-4 w-px bg-[var(--cin-line)]" aria-hidden="true" />
          <Link
            to={B2B_LINK.href}
            className="px-3 py-2 text-sm font-semibold text-[var(--cin-ink)] hover:text-[var(--cin-blue)] transition-colors inline-flex items-center gap-1"
          >
            {B2B_LINK.label}
          </Link>
          {/* Free tools — static page outside the SPA → <a> not <Link> */}
          <a
            href="/tools/"
            className="px-3 py-2 text-sm font-semibold text-[var(--cin-blue)] hover:opacity-80 transition-opacity inline-flex items-center gap-1"
          >
            כלים
          </a>
        </nav>

        {/* CTA + Cart + Mobile toggle */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Funnel entry point — a free consultation (the lab check itself is ₪150). */}
          <Link
            to="/diagnose"
            className="hidden md:inline-flex cin-btn-primary !py-2 !px-4 !text-sm"
          >
            ייעוץ חינם
          </Link>

          <button
            onClick={() => setOpen(!open)}
            className="md:hidden text-[var(--cin-ink)] p-2 rounded-lg focus-visible:ring-2 focus-visible:ring-[var(--cin-blue)]/40 focus-visible:outline-none"
            aria-label={open ? 'סגירת התפריט' : 'פתיחת התפריט'}
            aria-expanded={open}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden border-t border-[var(--cin-line)] bg-white/95 backdrop-blur-xl" role="dialog" aria-modal="true" aria-label="תפריט ניווט">
          <div className="px-5 py-6 space-y-1">
            {LINKS.map((l) => {
              const active = pathname === l.href;
              return (
                <Link
                  key={l.href}
                  to={l.href}
                  className={`block px-3 py-3 rounded-xl text-base font-medium transition-colors ${
                    active
                      ? 'bg-[var(--cin-blue)]/10 text-[var(--cin-blue)]'
                      : 'text-[var(--cin-ink)] hover:bg-[var(--cin-paper-soft)]'
                  }`}
                >
                  {l.label}
                </Link>
              );
            })}
            <div className="my-2 h-px bg-[var(--cin-line)]" aria-hidden="true" />
            <Link
              to={B2B_LINK.href}
              className="block px-3 py-3 rounded-xl text-base font-semibold text-[var(--cin-ink)] hover:bg-[var(--cin-paper-soft)] transition-colors"
            >
              {B2B_LINK.label}
            </Link>
            <a
              href="/tools/"
              className="block px-3 py-3 rounded-xl text-base font-semibold text-[var(--cin-blue)] hover:bg-[var(--cin-paper-soft)] transition-colors"
            >
              כלים חינמיים
            </a>
            <Link
              to="/diagnose"
              className="cin-btn-primary mt-4 w-full justify-center !flex"
            >
              ייעוץ חינם — מה כדאי לעשות עם המחשב
            </Link>
            <a
              href="https://wa.me/972546672863"
              data-track-from="header-mobile"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 block px-3 py-3 rounded-xl text-center text-base font-semibold text-[var(--cin-ink)] hover:bg-[var(--cin-paper-soft)] transition-colors"
            >
              או שאלה מהירה בוואטסאפ
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
